import React from "react";
import { Download, Image as ImageIcon, Film, ExternalLink } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Public demo carousel + video share page.
// Mobile browsers can long-press images / tap-and-hold the video to save.
// The "Download" buttons hit the backend endpoint with
// Content-Disposition: attachment to force the save dialog universally.

const SLIDES = [
  { file: "01_reserve.jpg",   title: "Reserve · 1:1 USD-backed",      subtitle: "$1,000,000 attested · HMAC-stamped" },
  { file: "02_contracts.jpg", title: "Contracts · live state machine", subtitle: "Roof Replacement IN_PROGRESS · Solar Install DRAFT" },
  { file: "03_create.jpg",    title: "Create wizard",                  subtitle: "Multi-milestone · USD-CBCU · USDC · APT" },
  { file: "04_aegis.jpg",     title: "Aegis Peace Token",              subtitle: "Live Solana mainnet · 999M supply" },
  { file: "05_proof.jpg",     title: "Truth Ledger",                   subtitle: "🌐 Web-cited · 12 real sources · HMAC signed" },
];

const VIDEOS = [
  {
    file: "walkthrough_long.mp4",
    title: "Full walkthrough · ~1:38 · AI responds live",
    subtitle: "Type a question · watch the safety pipeline + Honesty Tether self-rate in real-time",
    primary: true,
  },
  {
    file: "walkthrough.mp4",
    title: "Short walkthrough · 1:14 · 1080p",
    subtitle: "HUD · Ledger · Smart Contract · Aegis · Registry",
    primary: false,
  },
];

function downloadUrl(filename) {
  return `${BACKEND}/api/demo/download/${filename}`;
}

function viewUrl(filename) {
  // Direct asset URL — also works for long-press save on iOS
  return `/demo/${filename}`;
}

function CarouselSlide({ slide, index }) {
  return (
    <article
      className="border border-zinc-800 bg-[#0b0b0d]"
      data-testid={`demo-slide-${index + 1}`}
    >
      <div className="p-4 border-b border-zinc-900 flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">
            Slide {index + 1} of {SLIDES.length}
          </div>
          <div className="font-heading text-base text-zinc-100 mt-0.5">{slide.title}</div>
          <div className="text-[11px] text-zinc-500">{slide.subtitle}</div>
        </div>
        <a
          href={downloadUrl(slide.file)}
          download={slide.file}
          data-testid={`demo-download-${slide.file}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-emerald-500 text-emerald-300 hover:bg-emerald-500 hover:text-zinc-950 text-[10px] uppercase tracking-[0.25em] shrink-0"
        >
          <Download className="h-3 w-3" /> Save
        </a>
      </div>
      <a
        href={viewUrl(slide.file)}
        target="_blank"
        rel="noreferrer noopener"
        data-testid={`demo-image-link-${slide.file}`}
        className="block bg-black"
      >
        <img
          src={viewUrl(slide.file)}
          alt={slide.title}
          data-testid={`demo-image-${slide.file}`}
          className="w-full h-auto block"
          loading="lazy"
        />
      </a>
      <div className="p-3 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
        long-press the image → <span className="text-zinc-300">Save to Photos</span>
      </div>
    </article>
  );
}

function VideoBlock({ video }) {
  const tone = video.primary
    ? { wrap: "border-emerald-500/60 bg-emerald-500/5", head: "border-emerald-500/40", label: "text-emerald-300", btn: "border-emerald-500 text-emerald-300 hover:bg-emerald-500", hint: "text-emerald-300/80" }
    : { wrap: "border-sky-500/40 bg-sky-500/5", head: "border-sky-500/30", label: "text-sky-300", btn: "border-sky-500 text-sky-300 hover:bg-sky-500", hint: "text-sky-300/80" };
  return (
    <article className={`border ${tone.wrap}`} data-testid={`demo-video-block-${video.file}`}>
      <div className={`p-4 border-b ${tone.head} flex items-center justify-between gap-3`}>
        <div>
          <div className={`text-[10px] uppercase tracking-[0.3em] ${tone.label} inline-flex items-center gap-2`}>
            <Film className="h-3 w-3" /> {video.primary ? "★ recommended for YouTube" : "shorter alternate"}
          </div>
          <div className="font-heading text-base text-zinc-100 mt-0.5">{video.title}</div>
          <div className="text-[11px] text-zinc-500">{video.subtitle}</div>
        </div>
        <a
          href={downloadUrl(video.file)}
          download={video.file}
          data-testid={`demo-download-${video.file}`}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 border ${tone.btn} hover:text-zinc-950 text-[10px] uppercase tracking-[0.25em] shrink-0`}
        >
          <Download className="h-3 w-3" /> Save MP4
        </a>
      </div>
      <video
        src={viewUrl(video.file)}
        controls
        playsInline
        preload="metadata"
        data-testid={`demo-video-player-${video.file}`}
        className="w-full h-auto bg-black"
      />
      <div className={`p-3 text-[10px] uppercase tracking-[0.25em] ${tone.hint} leading-relaxed`}>
        on iphone · tap the share icon in the player → <span className="text-zinc-100">Save Video</span><br />
        on android · long-press the video → <span className="text-zinc-100">Download</span>
      </div>
    </article>
  );
}

export default function DemoShare() {
  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="text-[10px] uppercase tracking-[0.4em] text-emerald-400 mb-1">
          ⛓ SEAL · Public Share Surface
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-[0.15em]" data-testid="demo-page-title">
          Projectivy Sentinel · Demo Assets
        </h1>
        <p className="text-zinc-400 text-sm mt-2 max-w-2xl leading-relaxed">
          5 carousel slides (1080 × 1350) + the 74-second 1080p walkthrough. Long-press
          any image, or tap the <span className="text-emerald-300">Save</span> button on each card.
          Public surface — no auth needed, share the URL with anyone.
        </p>

        <div className="mt-8 space-y-6" data-testid="demo-assets">
          {VIDEOS.map((v) => <VideoBlock key={v.file} video={v} />)}
          {SLIDES.map((s, i) => <CarouselSlide key={s.file} slide={s} index={i} />)}
        </div>

        <div className="mt-10 pt-6 border-t border-zinc-900 text-[10px] uppercase tracking-[0.3em] text-zinc-600 leading-loose">
          <div>All assets generated by the Sentinel itself · regenerable after each phase ships</div>
          <a
            href="/"
            className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300 mt-2"
          >
            <ExternalLink className="h-3 w-3" /> back to sentinel
          </a>
        </div>
      </div>
    </div>
  );
}
